import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Film } from "lucide-react";
import { adminApi, getAdminToken } from "../../lib/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useSiteContent } from "../../context/SiteContentContext";
import { PROJECT_VIDEOS, YOUTUBE_CHANNEL } from "../../data/content";
import { getYoutubeId, youtubeThumb } from "../../lib/videoUtils";

function Field({ label, ...props }) {
  const Tag = props.rows ? "textarea" : "input";
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">{label}</span>
      <Tag className="cf-input mt-2" {...props} />
    </label>
  );
}

function persistableSrc(src = "") {
  if (!src || src.includes("/static/media/")) return "";
  return src;
}

function emptyVideo(prefix) {
  return {
    id: `${prefix}-${Date.now()}`,
    title: "",
    caption: "",
    src: "",
  };
}

function VideoEditor({ video, uploading, onChange, onUpload, extra }) {
  const youtubeId = getYoutubeId(video.src);
  return (
    <div className="grid lg:grid-cols-12 gap-5">
      <div className="lg:col-span-4">
        <div className="aspect-video rounded-2xl overflow-hidden bg-[#0A0A0A] grid place-items-center">
          {youtubeId ? (
            <img src={youtubeThumb(youtubeId)} alt="" className="w-full h-full object-cover" />
          ) : video.src ? (
            <video src={video.src} muted playsInline className="w-full h-full object-cover" />
          ) : (
            <Film className="text-white/40" />
          )}
        </div>
      </div>
      <div className="lg:col-span-8 space-y-4">
        <Field label="Title" value={video.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        <Field label="Caption" rows={3} value={video.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} />
        <Field
          label="Video URL (file or YouTube)"
          value={video.src || ""}
          onChange={(e) => onChange({ src: e.target.value })}
          placeholder="https://… or upload a file"
        />
        <label className="block">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Upload video file</span>
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="mt-2 block w-full text-sm"
            onChange={(e) => onUpload(e.target.files?.[0])}
            disabled={uploading}
          />
          <span className="text-xs text-black/45 mt-1 block">
            {uploading ? "Uploading…" : "MP4 or WebM, up to 80MB."}
          </span>
        </label>
        {extra}
      </div>
    </div>
  );
}

export default function AdminVideosPage() {
  const { can } = useAuth();
  const { videos, refresh } = useSiteContent();
  const [youtubeChannel, setYoutubeChannel] = useState(videos?.youtubeChannel || YOUTUBE_CHANNEL);
  const [homeVideo, setHomeVideo] = useState(videos?.homeVideo || emptyVideo("home"));
  const [serviceVideos, setServiceVideos] = useState(videos?.serviceVideos || []);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const token = useMemo(() => getAdminToken(), []);
  const canHome = can("videos.home", "edit");
  const canServices = can("videos.services", "edit");
  const canYoutube = canHome || canServices;

  useEffect(() => {
    setYoutubeChannel(videos?.youtubeChannel || YOUTUBE_CHANNEL);
    setHomeVideo(videos?.homeVideo || emptyVideo("home"));
    setServiceVideos(videos?.serviceVideos || []);
  }, [videos]);

  const updateService = (index, patch) => {
    setServiceVideos((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const moveService = (index, direction) => {
    setServiceVideos((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const upload = async (key, file, apply) => {
    if (!file) return;
    setUploadingKey(key);
    setMessage("");
    try {
      const src = await adminApi.uploadVideo(file);
      apply(src);
      setMessage("Video uploaded. Save to publish it on the site.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const existingHome = videos?.homeVideo || emptyVideo("home");
      const existingServices = videos?.serviceVideos || [];
      const home = canHome
        ? {
            id: homeVideo.id || "home-video",
            title: homeVideo.title || "Homepage video",
            caption: homeVideo.caption || "",
            src: persistableSrc(homeVideo.src),
            showOnHome: true,
            showOnServices: homeVideo.showOnServices !== false,
          }
        : {
            id: existingHome.id || "home-video",
            title: existingHome.title || "Homepage video",
            caption: existingHome.caption || "",
            src: persistableSrc(existingHome.src),
            showOnHome: true,
            showOnServices: existingHome.showOnServices !== false,
          };
      const extras = (canServices ? serviceVideos : existingServices).map((item, i) => ({
        id: item.id || `service-video-${i + 1}`,
        title: item.title || `Services video ${i + 1}`,
        caption: item.caption || "",
        src: persistableSrc(item.src),
        showOnHome: false,
        showOnServices: true,
      }));
      await adminApi.upsertContent(token, "videos", {
        youtubeChannel: canYoutube
          ? youtubeChannel.trim() || YOUTUBE_CHANNEL
          : videos?.youtubeChannel || YOUTUBE_CHANNEL,
        homeVideo: home,
        serviceVideos: extras,
        items: [home, ...extras],
      });
      await refresh();
      setMessage("Saved. Homepage and services videos update independently, unless the homepage clip is also ticked for services.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#EC4899]">CMS</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Project videos</h1>
      <p className="text-black/60 mt-3 max-w-2xl">
        Update the homepage film on its own. Tick it to also appear on services. Add extra services-only films in the second section — those never replace the homepage clip.
      </p>
      {message && (
        <p className={`mt-4 text-sm ${message.startsWith("Saved") || message.includes("uploaded") ? "text-emerald-700" : "text-[#BE185D]"}`}>
          {message}
        </p>
      )}

      <div className="space-y-6 mt-8">
        {!canHome && !canServices && (
          <p className="rounded-3xl bg-white border border-black/5 p-6 text-sm text-black/55">
            You can open Videos, but no video sections have been granted for this account. Ask a superadmin to update your permissions.
          </p>
        )}
        {canYoutube && (
        <section className="rounded-3xl bg-white border border-black/5 shadow-ambient p-6 md:p-8 space-y-6">
          <Field
            label="YouTube channel URL"
            value={youtubeChannel}
            onChange={(e) => setYoutubeChannel(e.target.value)}
            placeholder="https://www.youtube.com/@creativeflarebradford"
          />
        </section>
        )}

        {canHome && (
        <section className="rounded-3xl bg-white border border-black/5 shadow-ambient p-6 md:p-8 space-y-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">Homepage</p>
            <h2 className="font-display text-2xl mt-2">Homepage video</h2>
            <p className="text-sm text-black/55 mt-1">This is the clip on the home page. You can also send it through to services.</p>
          </div>
          <VideoEditor
            video={homeVideo}
            uploading={uploadingKey === "home"}
            onChange={(patch) => setHomeVideo((current) => ({ ...current, ...patch }))}
            onUpload={(file) => upload("home", file, (src) => setHomeVideo((current) => ({ ...current, src })))}
            extra={
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={homeVideo.showOnServices !== false}
                  onChange={(e) => setHomeVideo((current) => ({ ...current, showOnServices: e.target.checked }))}
                />
                Also show this video on the services page
              </label>
            }
          />
        </section>
        )}

        {canServices && (
        <section className="rounded-3xl bg-white border border-black/5 shadow-ambient p-6 md:p-8 space-y-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">Services</p>
            <h2 className="font-display text-2xl mt-2">Services videos</h2>
            <p className="text-sm text-black/55 mt-1">
              Extra films for the services page only. Edit, upload, reorder or remove these without changing the homepage.
            </p>
          </div>

          {serviceVideos.length === 0 && (
            <p className="text-sm text-black/45">No extra services videos yet. Add one below, or tick the homepage clip to show it here too.</p>
          )}

          {serviceVideos.map((item, i) => (
            <div key={item.id} className="rounded-3xl border border-black/8 p-5 md:p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">Services video {i + 1}</p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => moveService(i, -1)} className="w-9 h-9 grid place-items-center rounded-full border border-black/10" aria-label="Move up">
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" onClick={() => moveService(i, 1)} className="w-9 h-9 grid place-items-center rounded-full border border-black/10" aria-label="Move down">
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    className="text-sm text-[#BE185D] hover:underline"
                    onClick={() => setServiceVideos(serviceVideos.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <VideoEditor
                video={item}
                uploading={uploadingKey === item.id}
                onChange={(patch) => updateService(i, patch)}
                onUpload={(file) => upload(item.id, file, (src) => updateService(i, { src }))}
              />
            </div>
          ))}

          <div className="flex flex-wrap gap-3">
            {canServices && (
            <button
              type="button"
              onClick={() => setServiceVideos([...serviceVideos, emptyVideo("service")])}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Add services video
            </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (canYoutube) setYoutubeChannel(YOUTUBE_CHANNEL);
                if (canHome) setHomeVideo({ ...PROJECT_VIDEOS[0], showOnServices: true });
                if (canServices) {
                  setServiceVideos(PROJECT_VIDEOS.slice(1).map((video) => ({ ...video, showOnHome: false, showOnServices: true })));
                }
              }}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Load bundled videos
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save videos"}
            </button>
          </div>
        </section>
        )}
        {!canServices && (canHome || canYoutube) && (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save videos"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
